import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import React from 'react';
import { AvatarDropdown, AvatarName, Question } from '@/components';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import { getStoredUser, getToken, mapUserToCurrentUser } from '@/utils/auth';
import {
  getPublicExamEntryPath,
  isPublicExamHost,
  isPublicRoutePath,
  shouldUsePublicExamOrigin,
} from '@/utils/routes';
import { getCurrentSiteTitle, syncDocumentSiteTitle } from '@/utils/siteTitle';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
const publicExamEntryPath = getPublicExamEntryPath();

const getRuntimeSettings = (): Partial<LayoutSettings> => ({
  ...(defaultSettings as Partial<LayoutSettings>),
  title: getCurrentSiteTitle(),
});

const redirectPublicExamHost = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  const hostname = window.location.hostname;
  if (!isPublicExamHost(hostname)) {
    return false;
  }
  const { pathname, search } = history.location;
  if (pathname === '/' || pathname === '/welcome') {
    history.replace(`${publicExamEntryPath}${search}`);
    return true;
  }
  if (!isPublicRoutePath(pathname)) {
    history.replace(publicExamEntryPath);
    return true;
  }
  return false;
};

const resolveRequestBaseURL = () => {
  if (typeof window === 'undefined') {
    return ADMIN_API_BASE_URL;
  }
  if (
    shouldUsePublicExamOrigin(
      window.location.pathname,
      window.location.hostname,
    )
  ) {
    return window.location.origin;
  }
  if (isPublicRoutePath(window.location.pathname) && PUBLIC_EXAM_API_BASE_URL) {
    return PUBLIC_EXAM_API_BASE_URL;
  }
  return ADMIN_API_BASE_URL;
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  syncDocumentSiteTitle();

  const fetchUserInfo = async () => {
    const storedUser = getStoredUser();
    if (storedUser) {
      return mapUserToCurrentUser(storedUser);
    }
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (_error) {}
    return undefined;
  };
  if (redirectPublicExamHost()) {
    return {
      fetchUserInfo,
      settings: getRuntimeSettings(),
    };
  }
  // 如果不是登录页面，执行
  const { location } = history;
  const hasToken = Boolean(getToken());
  if (
    !isPublicRoutePath(location.pathname) &&
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = hasToken ? await fetchUserInfo() : undefined;
    return {
      fetchUserInfo,
      currentUser,
      settings: getRuntimeSettings(),
    };
  }
  return {
    fetchUserInfo,
    settings: getRuntimeSettings(),
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const currentSiteTitle = getCurrentSiteTitle();

  return {
    actionsRender: () => [<Question key="doc" />],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    footerRender: false,
    onPageChange: () => {
      syncDocumentSiteTitle();
      if (redirectPublicExamHost()) {
        return;
      }
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (
        !initialState?.currentUser &&
        location.pathname !== loginPath &&
        !isPublicRoutePath(location.pathname)
      ) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    menuHeaderRender: undefined,
    title: currentSiteTitle,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings: {
                    ...settings,
                    title: currentSiteTitle,
                  },
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  ...errorConfig,
  baseURL: resolveRequestBaseURL(),
};
